import React, { useState } from "react";
import "../../animation3d.css";
import doorsData from "../../data/3d.json";

export default function DoorExplorer() {
  const [doorIndex, setDoorIndex] = useState(0);
  const [activeLayer, setActiveLayer] = useState(0);
  const [explodeValue, setExplodeValue] = useState(60); // Control separation distance
  const [perspectivePreset, setPerspectivePreset] = useState("isometric"); // isometric, front, profile

  const currentDoor = doorsData[doorIndex];

  const nextDoor = () => {
    setDoorIndex((prev) => (prev === doorsData.length - 1 ? 0 : prev + 1));
    setActiveLayer(0);
  };

  const prevDoor = () => {
    setDoorIndex((prev) => (prev === 0 ? doorsData.length - 1 : prev - 1));
    setActiveLayer(0);
  };

  // Preset values for perspective viewing angles
  const getPerspectiveStyle = () => {
    switch (perspectivePreset) {
      case "front":
        return { transform: "rotateX(0deg) rotateY(0deg) rotateZ(0deg)" };
      case "profile":
        return { transform: "rotateX(5deg) rotateY(-80deg) rotateZ(0deg)" };
      case "isometric":
      default:
        return { transform: "rotateX(60deg) rotateY(0deg) rotateZ(-45deg)" };
    }
  };

  return (
    <section className="door-explorer-dashboard">
      {/* HEADER SECTION */}
      <header className="dashboard-header">
        <div className="brand">
          <span className="accent-dot"></span>
          <h1>ARCHITECTURAL 3D VISUALIZATION</h1>
          
        </div>
        <div className="door-selector">
          <button className="selector-nav-btn" onClick={prevDoor} aria-label="Previous door">
            ◀
          </button>
          <div className="door-meta-title">
            <span className="index-label">MODEL {doorIndex + 1}/{doorsData.length}</span>
            <h3>{currentDoor.name}</h3>
          </div>
          <button className="selector-nav-btn" onClick={nextDoor} aria-label="Next door">
            ▶
          </button>
        </div>
      </header>

      {/* MOBILE HORIZONTAL LAYER TABS (Visible <= 992px) */}
      <div className="explorer-mobile-tabs-container">
        <span className="explorer-mobile-tabs-label">Layers</span>
        <div className="explorer-mobile-tabs-scroll">
          {currentDoor.layers.map((layer, index) => {
            const isActive = activeLayer === index;
            return (
              <button
                key={index}
                className={`explorer-mobile-tab-btn ${isActive ? "active" : ""}`}
                onClick={() => setActiveLayer(index)}
              >
                0{index + 1} {layer.title.split(" ")[0]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="dashboard-body">
        {/* LEFT PANEL: LAYER TREE CONTROLLER (Desktop only, hidden on mobile <= 992px) */}
        <div className="control-panel">
          <div className="panel-header">
            <h3>Component Architecture</h3>
            <p>Select individual structural layers to inspect specifications.</p>
          </div>

          <div className="layer-list">
            {currentDoor.layers.map((layer, index) => {
              const isActive = activeLayer === index;
              return (
                <button
                  key={index}
                  className={`layer-tree-item ${isActive ? "active" : ""}`}
                  onClick={() => setActiveLayer(index)}
                >
                  <span className="layer-index">0{index + 1}</span>
                  <div className="layer-item-content">
                    <h4>{layer.title}</h4>
                    <span className="layer-badge">{layer.material}</span>
                  </div>
                  {isActive && <div className="active-glow-indicator" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* CENTER VIEWPORT: INTERACTIVE 3D COMPOSITE */}
        <div className="viewport-panel">
          <div className="viewport-canvas" onClick={() => setActiveLayer(0)}>
            <div className="viewport-grid-bg" />
            <div className="viewport-3d-scene">
              <div className="door-3d-model" style={getPerspectiveStyle()} onClick={(e) => e.stopPropagation()}>
                {currentDoor.layers.map((layer, index) => {
                  const midIndex = (currentDoor.layers.length - 1) / 2;
                  const zOffset = (midIndex - index) * explodeValue; // Symmetrically space layers on the Z-axis
                  const isHighlighted = activeLayer === index;
                  const isAnyActive = activeLayer !== null;

                  return (
                    <div
                      key={index}
                      className={`door-3d-layer ${isHighlighted ? "highlighted" : ""} ${
                        isAnyActive && !isHighlighted ? "dimmed" : ""
                      }`}
                      style={{
                        transform: `translateZ(${zOffset}px)`,
                        zIndex: currentDoor.layers.length - index,
                      }}
                      onClick={() => setActiveLayer(index)}
                    >
                      {/* Physical Layer Face */}
                      <div
                        className="layer-face"
                        style={{ background: layer.color }}
                      >
                        {/* Layer details mapped inside the 3D plane for realism */}
                        <div className="layer-face-overlay">
                          <span className="face-tag">0{index + 1}</span>
                          {index === 0 && (
                            <div className="decorative-door-handle" />
                          )}
                        </div>
                        {/* Thick edge rendering to make the sheets look 3D */}
                        <div className="layer-edge-right" />
                        <div className="layer-edge-bottom" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* DOCKED CONTROLS: Directly below the 3D canvas */}
          <div className="explorer-mid-controls" onClick={(e) => e.stopPropagation()}>
            <div className="explorer-presets">
              <button
                className={`explorer-preset-btn ${perspectivePreset === "isometric" ? "active" : ""}`}
                onClick={() => setPerspectivePreset("isometric")}
              >
                Isometric View
              </button>
              <button
                className={`explorer-preset-btn ${perspectivePreset === "front" ? "active" : ""}`}
                onClick={() => setPerspectivePreset("front")}
              >
                Front View
              </button>
              <button
                className={`explorer-preset-btn ${perspectivePreset === "profile" ? "active" : ""}`}
                onClick={() => setPerspectivePreset("profile")}
              >
                Edge Profile
              </button>
            </div>

            <div className="explorer-slider-container">
              <div className="slider-header">
                <label htmlFor="explode-slider">Explosion Gap</label>
                <span className="slider-value">{explodeValue}mm</span>
              </div>
              <input
                id="explode-slider"
                type="range"
                min="0"
                max="90"
                value={explodeValue}
                onChange={(e) => setExplodeValue(Number(e.target.value))}
                className="custom-range"
              />
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: LIVE ENGINEERING METRICS */}
        <div className="specs-panel">
          {currentDoor.layers[activeLayer] ? (
            <div className="layer-spec-card">
              <div className="spec-header">
                <span className="spec-category">Selected Layer Specifications</span>
                <h2>{currentDoor.layers[activeLayer].title}</h2>
              </div>

              <p className="spec-description">
                {currentDoor.layers[activeLayer].description}
              </p>

              <div className="metric-grid">
                <div className="metric-box">
                  <span className="metric-label">Material Composition</span>
                  <span className="metric-value">
                    {currentDoor.layers[activeLayer].material}
                  </span>
                </div>
                <div className="metric-box">
                  <span className="metric-label">Layer Thickness</span>
                  <span className="metric-value">
                    {currentDoor.layers[activeLayer].thickness}
                  </span>
                </div>
                <div className="metric-box">
                  <span className="metric-label">Unit Mass Density</span>
                  <span className="metric-value">
                    {currentDoor.layers[activeLayer].weight}
                  </span>
                </div>
              </div>

              <div className="certification-checklist">
                <h4>Verified Shield Standards</h4>
                <ul>
                  <li>
                    <span className="check-icon">✓</span> Acoustic Attenuation Certified
                  </li>
                  <li>
                    <span className="check-icon">✓</span> ISO Thermal Isolation Standard
                  </li>
                  <li>
                    <span className="check-icon">✓</span> High-Impact Force Absorption
                  </li>
                  <li>
                    <span className="check-icon">✓</span> Core Structural Warp Warranty
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="specs-placeholder">
              <p>Select a structural component layer from the menu to inspect engineering metrics.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}